import { useMemo, useState } from "react"
import { Outlet, useLocation, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	EmptyState,
	ErrorState,
	LoadingState
} from "~/components/screens/StateScreen"
import { Link } from "~/components/Link"
import { Button } from "~/components/ui/button"
import { FormPagination } from "~/components/forms/FormPagination"
import { useFormPagination } from "~/hooks/useFormPagination"
import { Editor } from "~/components/editor/Editor"
import { FormFillContext } from "~/components/forms/question-block"
import {
	formAnswersWithDefaults,
	formQuestionsFromContent,
	formShareReservedPaths,
	validateFormAnswers
} from "~/lib/forms"
import { api, type RouterInputs } from "~/trpc/react"

export function FormDomainOutlet() {
	const { pathname } = useLocation()
	const path = pathname.match(/^\/([a-zA-Z0-9-]{1,100})\/?$/)?.[1] ?? ""
	const canBeFormPath =
		Boolean(path) && !formShareReservedPaths.includes(path.toLowerCase())
	const domain = api.forms.publicDomain.useQuery(undefined, {
		enabled: canBeFormPath,
		retry: false,
		staleTime: 60_000
	})
	if (!canBeFormPath) return <Outlet />
	if (domain.isLoading) return <LoadingState scope="page" />
	if (domain.isError) return <ErrorState title="Unable to load this page" />
	return domain.data ? (
		<FormSharePage key={path} customPath={path} />
	) : (
		<Outlet />
	)
}

export default function FormSharePage({ customPath }: { customPath?: string }) {
	const location = useLocation()
	const { shareId = "", sharePath: routePath = "" } = useParams<{
		shareId: string
		sharePath: string
	}>()
	const sharePath = customPath ?? routePath
	const [answers, setAnswers] = useState<{ fieldId: string; value: unknown }[]>(
		[]
	)
	const [submitted, setSubmitted] = useState<string | null>(null)
	const share = api.forms.publicShare.useQuery(
		{ shareId },
		{ enabled: Boolean(shareId) }
	)
	const shareByPath = api.forms.publicShareByPath.useQuery(
		{ path: sharePath },
		{ enabled: Boolean(sharePath) }
	)
	const submit = api.forms.publicSubmit.useMutation({
		onSuccess: (result) => {
			if (!result) {
				toast.error("This form link is no longer available.")
				return
			}
			setSubmitted(result.id)
			setAnswers([])
		},
		onError: (error) =>
			toast.error(error.message || "Could not submit the form")
	})
	const uploadMutation = api.forms.uploadAttachment.useMutation()
	const shareQuery = shareId ? share : shareByPath
	const resolved = shareQuery.data
	const questions = useMemo(
		() => formQuestionsFromContent(resolved?.form.content),
		[resolved?.form.content]
	)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const pagination = useFormPagination(
		resolved?.form.content,
		setErrors,
		answers
	)
	const fillState = useMemo(
		() => ({
			filling: true,
			pagination,
			upload: async (input: { name: string; type: string; data: string }) =>
				uploadMutation.mutateAsync({
					shareId: shareId || (resolved?.id ?? ""),
					name: input.name,
					type: input.type,
					data: input.data
				}),
			fields: questions,
			errors,
			answers: formAnswersWithDefaults(questions, answers),
			setAnswer: (fieldId: string, value: unknown) =>
				setAnswers((current) => [
					...current.filter((answer) => answer.fieldId !== fieldId),
					{ fieldId, value }
				])
		}),
		[
			answers,
			questions,
			errors,
			shareId,
			resolved?.id,
			uploadMutation,
			pagination
		]
	)
	if (shareQuery.isLoading) return <LoadingState scope="page" />
	if (shareQuery.error?.data?.code === "UNAUTHORIZED")
		return (
			<EmptyState
				scope="page"
				title="Sign in required"
				description={shareQuery.error.message}
				actions={
					<Button asChild size="lg" className="w-full">
						<Link
							to={`/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}`}
						>
							Sign in
						</Link>
					</Button>
				}
			/>
		)
	if (shareQuery.error?.data?.code === "FORBIDDEN")
		return (
			<EmptyState
				scope="page"
				title="You don't have access to this form"
				description={shareQuery.error.message}
			/>
		)
	if (shareQuery.error)
		return (
			<main className="flex min-h-svh items-center justify-center p-6">
				<ErrorState
					title="Unable to load this form"
					description="Please try again."
					actions={
						<Button
							className="w-full"
							pending={shareQuery.isFetching}
							onClick={() => void shareQuery.refetch()}
						>
							Try again
						</Button>
					}
				/>
			</main>
		)
	if (!resolved)
		return (
			<EmptyState
				scope="page"
				title="This form link is unavailable"
				description="Ask the form owner for a current link."
			/>
		)
	const data = resolved.form
	const submitForm = () => {
		const result = validateFormAnswers(questions, answers)
		setErrors(result.errors)
		if (!result.ok) {
			toast.error(
				result.errors.form || "Check the highlighted questions and try again."
			)
			return
		}
		submit.mutate({
			shareId: resolved.id,
			answers:
				result.answers as RouterInputs["forms"]["publicSubmit"]["answers"]
		})
	}
	return (
		<main className="min-h-screen bg-background-secondary px-5 py-8">
			<div className="mx-auto max-w-4xl rounded-xl border border-stroke bg-background p-6 shadow-lg sm:p-8">
				<div className="mb-6 border-b border-stroke pb-5">
					<h1 className="text-3xl font-semibold text-text-main">{data.name}</h1>
					{data.description ? (
						<p className="mt-2 text-text-sub">{data.description}</p>
					) : null}
				</div>
				{submitted ? (
					<div className="py-8 text-center">
						<h2 className="text-lg font-semibold text-text-main">
							Submission saved
						</h2>
						<p className="mt-1 text-sm text-text-sub">
							Thank you for your answers.
						</p>
					</div>
				) : (
					<FormFillContext.Provider value={fillState}>
						<Editor
							key={resolved.id}
							variant="form"
							teamId={""}
							initialState={data.content}
							disabled
						/>
						<div className="mt-4">
							<FormPagination
								pending={submit.isPending}
								disabled={uploadMutation.isPending}
								onSubmit={submitForm}
							/>
						</div>
					</FormFillContext.Provider>
				)}
			</div>
		</main>
	)
}
